import React from "react";
import { Button, Input } from "semantic-ui-react";
import { LabelledRow } from "../../components";
import moment from "moment";
import PaymentMethodDropdown from "../../components/dropdowns/reactselect/PaymentMethodDropdown";
import ValidatorContext from "formvalidation/FormContext";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import { useValidators } from "formvalidation/hooks";
import { FormDatePicker } from "components/DatePicker/SingleDatePicker";

interface Props {
  invoiceId: number
  currency: server.dto.Currencies
  outstandingAmount: number
  existingPayment?: server.dto.Payment
  onSaved: () => void
}

const defaultPaymentMethod = serverReference.paymentMethods.find(p => p.value === "bankTransfer");

const App = ({ invoiceId, currency, outstandingAmount, existingPayment, onSaved }: Props) => {
  const [receivedOn, setReceivedOn] = React.useState<moment.Moment>(existingPayment == null ? moment.utc() : moment.utc(existingPayment.receivedOn));
  const [description, setDescription] = React.useState<string>(existingPayment?.description ?? "");
  const [paymentMethod, setPaymentMethod] = React.useState<server.dto.PaymentMethod>(existingPayment?.method ?? defaultPaymentMethod);
  const [amount, setAmount] = React.useState<number>(existingPayment ? existingPayment.amount.amount : outstandingAmount);
  const [isSaving, setIsSaving] = React.useState(false);
  const validators = useValidators();
  const [enterPressed, setEnterPressed] = React.useState(false);
  const onSubmit = (e?) => {
    e?.preventDefault();
    savePayment();
  };

  const savePayment = async () => {
    setIsSaving(true);

    const validationResult = await validators.validateAll();
    if (validationResult.hasErrors) {
      setIsSaving(false);
      return;
    }

    const isNewPayment = existingPayment == null;

    if (isNewPayment) {
      const createPayment: server.dto.CreatePaymentInstruction = {
        invoiceId,
        receivedOn: receivedOn.toDate(),
        method: paymentMethod.value,
        description,
        amount
      };
      await window.CoreApi.Payment.Create(createPayment);
    } else {
      const updatePayment: server.dto.UpdatePaymentInstruction = {
        paymentId: existingPayment.paymentId,
        receivedOn: receivedOn.toDate(),
        method: paymentMethod.value,
        description,
        amount
      };
      await window.CoreApi.Payment.Update(updatePayment);
    }

    onSaved();
  };

  const onEnterPressed = () => {
    setEnterPressed(true);
  };

  // We use this workaround because first onblur the numeric input, and next render we submit the form
  React.useEffect(() => {
    if (enterPressed && !isSaving) {
      setEnterPressed(false);
      savePayment();
    }
  }, [enterPressed]);

  return <>
    <div>
      <ValidatorContext settings={validators.validatorSettings}>
        <table className="s1_table bordergrid gray">
          <tbody>
            <LabelledRow label={RESX.Payment.ReceivedOn}>
              <FormDatePicker
                validatorKey="receivedOn"
                minDate={moment.utc(serverReference.constants.payment.minYearDate)}
                maxDate={moment.utc(serverReference.constants.payment.maxYearDate)}
                value={receivedOn}
                onChange={setReceivedOn}
              />
            </LabelledRow>
            <LabelledRow label={RESX.GeneralLabels.Description}>
              <Input
                onKeyDown={e => { e.key === "Enter" && setEnterPressed(true); }}
                fluid
                value={description}
                maxLength={serverReference.constants.payment.maxDescriptionLength}
                onChange={(_, { value }) => { setDescription(value); }}
              />
            </LabelledRow>
            <LabelledRow label={RESX.Payment.PaymentMethod}>
              <PaymentMethodDropdown
                value={paymentMethod}
                onChange={setPaymentMethod}
              />
            </LabelledRow>
            <LabelledRow label={RESX.Payment.Amount}>
              <RequiredNumberInput
                width={90}
                validatorKey="amount"
                value={amount}
                onChange={v => { setAmount(v); }}
                onEnterPressed={onEnterPressed}
                decimalPoints={2}
                labelContent={currency.toUpperCase()}
                canBeNegative
              />
            </LabelledRow>
          </tbody>
        </table>
      </ValidatorContext>
    </div>
    <div className="flex margin-top-bigger">
      <div className="grow1"></div>
      <Button
        primary
        loading={isSaving}
        disabled={isSaving}
        onClick={onSubmit}
      >{RESX.Buttons.btnSave}</Button>
    </div>
  </>;
};

export default App;